/**
 * Space-saving layout of the comments panel
 * (operator-task-fe-comment-panel-space-saving-collapse-composer-and-compact-assign,
 * Margot): the new-comment composer is collapsed behind an "Add comment" button
 * while the user is only reading, and the "Assign to" picker is shown only on
 * demand or when an assignee is set — never as an empty dropdown.
 *
 * The decisions are pure functions so they can be unit-tested without rendering
 * the whole panel (mirroring commentCancel / commentEdit / commentRead).
 */

export interface ComposerState {
    // The user clicked "Add comment" (or otherwise opened the composer).
    expanded: boolean;
    draft: string;
    // A reply is armed (clicking "Reply" on a comment opens the composer).
    replying: boolean;
    assigneeDraft: string | null;
}

// The composer is open when the user asked for it, OR when it holds anything
// that must stay visible: typed text (even whitespace — never hide input the
// user is producing), an armed reply, or a chosen assignee. So collapsing can
// never hide a half-written comment.
export const isComposerOpen = ({
    expanded,
    draft,
    replying,
    assigneeDraft,
}: ComposerState): boolean => expanded || draft.length > 0 || replying || assigneeDraft !== null;

// The "Assign to" picker shows only when the user asked for it ("Assign"
// button) or an assignee is already set. Otherwise only the small "Assign"
// button is rendered.
export const isAssigneePickerShown = (requested: boolean, assignee: string | null): boolean =>
    requested || assignee !== null;

// While a post is in flight the WHOLE composer is locked — textarea, assignee
// picker, Cancel/Assign — not only the submit button: the request has already
// captured the draft, so an edit made mid-flight would be silently dropped.
// Links (the docs tooltip) stay usable. Unlocks again when the request
// resolves (success or error), so a failed post can be retried or edited.
export const isComposerLocked = (isDisabled: boolean, isSubmitting: boolean): boolean =>
    isDisabled || isSubmitting;
